import { Badge, Box, Button, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import moment from 'moment'
import { ReactComponent as BellIcon } from '../../../../assets/svg/bell.svg'
import { useBoundStore } from '../../../../store'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom';
import eventEmitter from '../../../../utils/eventEmitter';
import { update } from 'lodash'

export function NotificationControl() {
  const DEFAULT_NO_NOTIFICATIONS = 'You have no notifications'
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const { notifications, getNotifications, deleteNotification, clearAllNotifications, updateNotificationStatus } = useBoundStore((state) => ({
    notifications: state.notifications,
    getNotifications: state.getNotifications,
    deleteNotification: state.deleteNotification,
    clearAllNotifications: state.clearAllNotifications,
    updateNotificationStatus: state.updateNotificationStatus,
  }))
  //const notificationCount = Array.isArray(notifications) ? notifications.length : 0

  // count of notifications with status false
  const notificationCount = Array.isArray(notifications) ? notifications.filter((item:any) => !item.status).length : 0

  const hasNotifications = Array.isArray(notifications) && notifications.length > 0

  const navigate = useNavigate();

  useEffect(() => {
    getNotifications();

    const listener = (message: any) => {
      // console.log('Notification received', message);
      getNotifications();
    };

    eventEmitter.on('notification', listener);

    return () => {
      eventEmitter.off('notification', listener);
    };
  }, [])

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    updateNotificationStatus();
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // useEffect(() => {
  //   //console.log(notifications)
  // }, [notifications])

  const handleDelete = (id: number) => {
    deleteNotification(id)
  }

  const handleClearAll = () => {
    clearAllNotifications()
  }

  // Where an inbox row opens. Mirrors the backend's buildNotificationDeepLink
  // (the Slack "Open in portal" link and the email button): most payloads store
  // the bare projects base and the ids the event concerns, so land on the
  // project — and, for a video event, on its downloads tab with the file's
  // review open — instead of the projects index. A payload whose path already
  // embeds ids or a query string is used as-is (never double the id).
  const PROJECTS_BASE = '/video-production/projects';
  // A deleted project's notification must stay on the projects list — its
  // detail page 404s. New ones carry no projectId, so the check below already
  // keeps them there; older ones still do, and are recognisable only by their
  // title, which deleteProjectCore has always set to this exact string.
  // Notification rows store no kind/action column to key on.
  const isDeletedProject = (item: any) => item?.title === 'Project Deleted';
  const deepLinkFor = (item: any, payload: any): string | undefined => {
    const path = typeof payload?.path === 'string' ? payload.path : undefined;
    if (path !== PROJECTS_BASE || !payload?.projectId || isDeletedProject(item)) return path;
    const fileId =
      payload.fileId ||
      (Array.isArray(payload.fileIds) && payload.fileIds.length === 1 ? payload.fileIds[0] : null);
    if (fileId) {
      let link = `${PROJECTS_BASE}/${payload.projectId}?tab=downloads&review=${fileId}`;
      // Comment anchor + seek offset, the same as the Slack and email links.
      if (payload.commentId) {
        link += `&comment=${payload.commentId}`;
        if (payload.timestamp != null) link += `&t=${payload.timestamp}`;
      }
      return link;
    }
    if (Array.isArray(payload.fileIds) || Array.isArray(payload.internal_statuses)) {
      return `${PROJECTS_BASE}/${payload.projectId}?tab=downloads`;
    }
    return `${PROJECTS_BASE}/${payload.projectId}`;
  }

  const handleClick = (item: any) => {
    let path: string | undefined;
    try {
      const payload = item?.payload ? JSON.parse(item.payload) : {};
      path = deepLinkFor(item, payload);
    } catch (e) {
      console.error('Failed to parse notification payload', e, item);
    }
    if (path) {
      navigate(path);
    }
    handleClose();
  }

  // Only notifications sent to internal users carry internal statuses in
  // their payload, so no extra role gating is needed here. Prefers the
  // structured per-channel list; old rows only have the legacy string.
  const getInternalStatuses = (item: any): Array<{ channel?: string; status: string }> => {
    try {
      const payload = item?.payload ? JSON.parse(item.payload) : {};
      if (Array.isArray(payload?.internal_statuses)) {
        return payload.internal_statuses.filter((entry: any) => entry && entry.status);
      }
      if (payload?.internal_status) {
        return [{ status: payload.internal_status }];
      }
      return [];
    } catch (e) {
      return [];
    }
  }

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="primary"
      >
        <Badge badgeContent={notificationCount} color="error">
          <BellIcon />
        </Badge>
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        // transformOrigin={{
        //     vertical: 5,
        //     horizontal: 'left',
        // }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{ marginTop: 3 }}
      >
        {hasNotifications && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 0.5 }}>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleClearAll()
              }}
              sx={{ textTransform: 'none' }}
            >
              Clear all
            </Button>
          </Box>
        )}

        {hasNotifications &&
          notifications!.map((item) => (
            <MenuItem
              key={item.id}
              sx={{
                width: '325px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
              }}
              onClick={() => handleClick(item)}
            >
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  fontWeight="bold"
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    // Project notifications put Project / Content Type /
                    // Product / Channel on their own lines
                    whiteSpace: 'pre-line',
                    wordWrap: 'break-word',
                  }}
                >
                  {item.content}
                </Typography>
                {getInternalStatuses(item).length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {getInternalStatuses(item).map((entry, idx) => (
                      <Box
                        key={`${entry.channel || ''}-${entry.status}-${idx}`}
                        sx={{
                          backgroundColor: 'var(--tint)',
                          color: 'var(--accent-text)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          px: 1,
                          py: 0.25,
                        }}
                      >
                        {entry.channel ? `${entry.channel}: ${entry.status}` : entry.status}
                      </Box>
                    ))}
                  </Box>
                )}
                <Typography variant="caption">{moment(item.createdAt).fromNow()}</Typography>
              </Stack>
              <IconButton
                size="small"
                sx={{
                  color: 'grey.500',
                  fontSize: '16px',
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleDelete(item.id)
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))}

        {!hasNotifications && <MenuItem>{DEFAULT_NO_NOTIFICATIONS}</MenuItem>}
      </Menu>
    </Box>
  )
}
